<template>
  <AccountContextWrapper>
    <template #default>
      <div class="row">
        <div class="col-sm-12">
          <div
            class="d-flex flex-wrap justify-content-center align-items-center px-3 py-2 border-bottom bg-white gap-3"
          >
            <div class="d-flex align-items-center gap-1">
              <h5 class="mb-0 h6 fw-bold">Monthly Overview</h5>
              <IconInfoCircle
                :size="14"
                v-b-tooltip.hover
                title="Before loading the transactions, make sure to 1) create a transaction with 'internal adjustments' (where you send money from splurges to other categories or from any other cat to other cats). 2) Make sure to create a transaction that covers all of the transactions in the HYSA account. 3) Create the end of transaction transaction and don't forget to place the total (in negative) in the pending category."
              />
            </div>
            <BFormGroup
              id="input-group-snapshot-filter"
              label="Snapshot:"
              label-for="snapshot-filter"
              class="floating-group"
            >
              <BFormSelect
                id="snapshot-filter"
                v-model="selectedSnapshot"
                :options="snapshotsOpts"
                size="sm"
                style="font-size: 0.85rem"
              />
            </BFormGroup>
            <BButton
              variant="outline-success"
              size="sm"
              @click="txnLoaderModal = true"
            >
              Transactions Loader
            </BButton>
            <BButton
              variant="outline-success"
              size="sm"
              @click="endOfMonthTxnModal = true"
            >
              End of Month Transaction
              <IconInfoCircle
                :size="14"
                v-b-tooltip.hover
                title="Manually creates a budget template transaction dated to the last day of the month. Select an institution, set an amount/description, pick a budget template to split amounts across spending categories, then save — creates the transaction and attaches category breakdowns."
                @click.stop
              />
            </BButton>
            <BFormGroup
              id="input-group-snapshot-field"
              label="Budget Snapshot:"
              label-for="snapshot-field"
              class="floating-group"
            >
              <BInputGroup>
                <BFormInput
                  id="snapshot-field"
                  v-model="snapshotAlias"
                  type="text"
                  size="sm"
                  style="font-size: 0.85rem"
                  :class="{ 'is-invalid': snapshotAliasError !== null }"
                />
                <BButton
                  variant="primary"
                  size="sm"
                  :loading="savingSnapshotAlias"
                  @click="createBudgetSnapshot"
                  ><IconHandClick :size="14"
                /></BButton>
              </BInputGroup>
            </BFormGroup>
          </div>
        </div>
        <div class="col-sm-12" id="monthly-review-table-div">
          <BTable
            v-if="selectedSnapshot && tableItems && tableFields"
            :items="tableItems || []"
            :fields="tableFields || []"
            sticky-header
          >
            <template #cell(transactions)="row">
              <p class="mb-0">
                {{ row.item.transactions.amount }}
                <IconInfoCircle
                  :size="14"
                  v-b-tooltip.click
                  :title="`${row.item.transactions.description} on ${row.item.transactions.date}`"
                />
              </p>
            </template>
            <template #head()="data">
              <!-- Header Label -->
              <p
                class="mb-0"
                :style="{ whiteSpace: 'nowrap' }"
                v-b-tooltip.click
                :title="`${data.label.split(',,')[0]}`"
              >
                {{ data.label.split(",,")[0].substring(0, 9) }}
              </p>
              <!-- Budget Amount from Snapshot (from previous month)-->
              <p class="mb-0 custom-table-header-budget-amount">
                {{ formatCurrencyDollarSign(data.label.split(",,")[1]) }}
              </p>
              <!-- Leftover amount of each category after applying transactions to prev month's snapshot -->
              <p
                class="mb-0 custom-table-header-budget-amount"
                v-b-tooltip.click
                :title="`Accumulated this month: ${data.label.split(',,')[3]}`"
              >
                {{ formatCurrencyDollarSign(data.label.split(",,")[2]) }}
              </p>
            </template>
          </BTable>
        </div>
      </div>
      <TrsxnsLoaderModal
        v-if="acctUid"
        v-model="txnLoaderModal"
        :acctUid="acctUid"
        @trsxnsLoaded="handleTrsxnsLoad"
      />
      <EndOfMonthTrsxnModal
        v-if="acctUid"
        v-model="endOfMonthTxnModal"
        :acctUid="acctUid"
      />
    </template>
  </AccountContextWrapper>
</template>
<script>
import AccountContextWrapper from "@/components/molecules/AccountContextWrapper.vue";
import TrsxnsLoaderModal from "@/components/MonthlyOverview/TrsxnsLoaderModal.vue";
import EndOfMonthTrsxnModal from "@/components/MonthlyOverview/EndOfMonthTrsxnModal.vue";
import {
  BTable,
  BFormGroup,
  BInputGroup,
  BFormInput,
  BFormSelect,
  BButton,
  vBTooltip,
} from "bootstrap-vue-next";
import {
  userAccount as userAcctNs,
  userSession as userSessionNs,
  bankingInstitutions as bankingInstitutionsNs,
} from "@/store/namespaces";
import debounce from "lodash.debounce";
import { IconInfoCircle, IconHandClick } from "@tabler/icons-vue";
import numberFormatMixin from "@/mixins/numberFormatMixin";

export default {
  name: "IndexView",
  components: {
    BTable,
    AccountContextWrapper,
    BFormGroup,
    BFormSelect,
    BInputGroup,
    BFormInput,
    TrsxnsLoaderModal,
    BButton,
    IconInfoCircle,
    IconHandClick,
    EndOfMonthTrsxnModal,
  },
  mixins: [numberFormatMixin],
  data() {
    return {
      txnLoaderModal: false,
      endOfMonthTxnModal: false,
      createBudgetSnapshotModal: false,
      selectedSnapshot: null,
      fetchedSnapshots: null,
      tableItems: null,
      tableFields: null,
      snapshotAlias: null,
      snapshotAliasError: null,
      savingSnapshotAlias: false,
    };
  },
  directives: {
    BTooltip: vBTooltip,
  },
  computed: {
    acctUid() {
      return this.$store.getters[`${userAcctNs}/getSelectedAcctUid`];
    },
    userInfo() {
      return this.$store.getters[`${userSessionNs}/getUserInfo`];
    },
    snapshotsOpts() {
      if (!this.fetchedSnapshots) {
        return [];
      }

      return this.fetchedSnapshots.map((snapshot) => ({
        value: snapshot.item,
        text: snapshot.item.alias,
      }));
    },
  },
  watch: {
    // Make sure to refetch transactions when the selected account changes
    acctUid(newAcctUid, oldAcctUid) {
      if (newAcctUid !== oldAcctUid) {
        this.fetchSnapshots();
        this.fetchBankingInstitutions();
      }
    },
  },
  mounted() {
    this.fetchSnapshots();
    this.fetchBankingInstitutions();
  },
  methods: {
    fetchBankingInstitutions: debounce(async function () {
      if (!this.acctUid) {
        console.warn(
          "Cannot fetch banking institutions because acctUid is not defined."
        );
        return;
      }

      try {
        const params = {
          filter: [`acctUid:eq:${this.acctUid}`],
        };
        await this.$store.dispatch(
          `${bankingInstitutionsNs}/fetchBankingInstitutions`,
          {
            qryParams: params,
          }
        );
      } catch (error) {
        console.error("Error fetching banking institutions:", error);
      }
    }, 300),
    fetchSnapshots: debounce(async function () {
      if (!this.acctUid) {
        console.warn("Cannot fetch snapshots because acctUid is not defined.");
        return;
      }

      try {
        const response = await this.$api.get("/budget-snapshots", {
          params: {
            filter: [`acctUid:eq:${this.acctUid}`],
          },
        });

        this.fetchedSnapshots = response.data;
      } catch (error) {
        console.error("Error fetching snapshots:", error);
      }
    }, 300),
    handleTrsxnsLoad(trsxns) {
      if (!trsxns || trsxns.length === 0) {
        this.$notify.notifyError("No transactions to load.");
        return;
      }

      if (!this.selectedSnapshot) {
        this.$notify.notifyError("No snapshot selected.");
        return;
      }

      // Create the individual category rows for the table items based on the transactions loaded
      const categoriesRows = this.createCategoriesRows(trsxns);

      // Accumulate categories amounts into the first row of the table items
      const categoriesAccumulations =
        this.createCategoriesAccumulations(categoriesRows);

      // Create the table's headers based on the selected snapshot categories and their budget amounts
      const tableFields = this.createTableHeaders(
        categoriesRows,
        categoriesAccumulations
      );

      this.tableFields = tableFields;
      this.tableItems = categoriesRows;
    },
    createCategoriesRows(trsxns) {
      const categoriesRows = [];
      trsxns.forEach((t) => {
        const catsObj = { transactions: t };
        const cats = t.snapshot_categories;
        if (cats && cats.length > 0) {
          cats.forEach((c) => {
            catsObj[c.spending_category_uid] = c.fixed_amount;
          });
        }
        categoriesRows.push(catsObj);
      });

      return categoriesRows;
    },
    createTableHeaders(rows, accs) {
      const headers = [];
      let origCatsSum = 0;
      let catsSumAfterTrsxns = 0;
      this.selectedSnapshot.categories.forEach((category) => {
        const sum = category.budget_amount + (accs[category.category_uid] || 0);
        origCatsSum += category.budget_amount;
        catsSumAfterTrsxns += sum;

        if (category.category_uid === "pending") {
          // Add pending category at the beginning of the categories
          headers.unshift({
            key: category.category_uid,
            label: `${category.name},,${category.budget_amount},,${sum},,${
              accs[category.category_uid] || "--"
            }`,
          });
          return;
        }

        headers.push({
          key: category.category_uid,
          label: `${category.name},,${category.budget_amount},,${sum},,${
            accs[category.category_uid] || "--"
          }`,
        });
      });

      headers.unshift({
        key: "transactions",
        label: `Transactions,,${origCatsSum},,${catsSumAfterTrsxns},,${
          accs.transactions || 0
        }`,
      });

      return headers;
    },
    createCategoriesAccumulations(trsxnsItems) {
      const accs = {};
      let totalAmount = 0;

      trsxnsItems.forEach((item) => {
        for (const [key, value] of Object.entries(item)) {
          if (key === "transactions") continue;

          if (!accs[key]) {
            accs[key] = value;
          } else {
            accs[key] += value;
          }
          totalAmount += value;
        }
      });

      accs.transactions = totalAmount;
      return accs;
    },
    async createBudgetSnapshot() {
      if (!this.selectedSnapshot) {
        this.$notify.notifyError(
          "Please select a snapshot to base the new snapshot on."
        );
        return;
      }

      if (!this.tableFields || this.tableFields.length === 0) {
        this.$notify.notifyError(
          "No transactions loaded to create a snapshot."
        );
        return;
      }

      if (!this.snapshotAlias || this.snapshotAlias.trim() === "") {
        this.snapshotAliasError = "Snapshot alias cannot be empty.";
        return;
      }

      this.snapshotAliasError = null;
      try {
        this.savingSnapshotAlias = true;
        const userUid = this.userInfo?.id;
        if (!userUid) {
          this.$notify.notifyError("User information is not available.");
          return;
        }

        // Use calculated leftover amounts for each category as the budget amounts for the new snapshot
        const categoriesForSnapshot = this.tableFields
          .filter((field) => field.key !== "transactions")
          .map((field) => {
            const categoryUid = field.key;
            const labelParts = field.label.split(",,");
            const leftoverAmount = parseFloat(labelParts[2]).toFixed(2) || 0; // Get the leftover amount from the header label
            return {
              categoryUid: categoryUid,
              budgetAmount: leftoverAmount,
              name: labelParts[0],
            };
          });

        const payload = {
          acctUid: this.acctUid,
          userId: userUid,
          alias: this.snapshotAlias,
          categories: categoriesForSnapshot,
        };

        await this.$api.post("/budget-snapshots", payload);
        this.$notify.notifySuccess("Budget snapshot created successfully.");
      } catch (error) {
        console.error("Error validating snapshot alias:", error);
        this.$notify.notifyError(
          "An error occurred while validating the snapshot alias."
        );
      } finally {
        this.savingSnapshotAlias = false;
      }
    },
  },
};
</script>
<style>
#monthly-review-table-div div.b-table-sticky-header {
  max-height: 700px !important;
}
#monthly-review-table-div table th,
#monthly-review-table-div table td {
  font-size: 0.75rem;
}
</style>
